import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { shortDate } from "@/lib/datetime"
import { withDeadlineTime } from "@/lib/bid-deadline"
import { BidStagePill } from "@/components/dashboard/BidStagePill"
import {
  BOXED_ROW_CLASS,
  BoxedList,
  BoxedListEmpty,
  SectionHeader,
} from "@/components/dashboard/DashboardSection"
import {
  DEADLINE_WINDOW_DAYS,
  DUE_SOON_DAYS,
  partitionDeadlines,
  type DeadlineRow,
} from "@/components/dashboard/deadlines"
import type { Bid } from "@/lib/schemas"

interface DeadlineListProps {
  bids: Bid[]
  /** Injectable clock; defaults to the current time. */
  now?: Date
}

const ROW_CAP = 6

function DeadlineRowLink({ row, overdue }: { row: DeadlineRow; overdue: boolean }) {
  const dueSoon = !overdue && row.days <= DUE_SOON_DAYS
  const dotColor = overdue
    ? "bg-destructive"
    : dueSoon
      ? "bg-[hsl(var(--status-warning))]"
      : "bg-muted-foreground/40"
  return (
    <Link
      to={`/app/bids/${row.bid.id}`}
      className={cn("flex items-center gap-2.5", BOXED_ROW_CLASS)}
      data-testid={overdue ? "deadline-overdue-row" : "deadline-upcoming-row"}
    >
      <span aria-hidden className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotColor)} />
      <span className="min-w-0 flex-1 truncate text-[13px]">{row.bid.title}</span>
      <BidStagePill bid={row.bid} />
      {overdue ? (
        <span className="shrink-0 whitespace-nowrap text-[11px] font-medium tabular-nums text-destructive">
          {/* A closing time passed earlier today is overdue before a day has gone by. */}
          {row.days < 0 ? `${Math.abs(row.days)}d overdue` : "Overdue"}
        </span>
      ) : (
        <span
          className={cn(
            "shrink-0 whitespace-nowrap text-[11px]",
            dueSoon ? "text-[hsl(38_85%_35%)]" : "text-muted-foreground",
          )}
        >
          {withDeadlineTime(shortDate(row.bid.deadline), row.bid)}
        </span>
      )}
    </Link>
  )
}

// One boxed list: overdue bids first, then the next 60 days, soonest first.
// Capped at ROW_CAP rows, with an "N more" row into Bids for the rest.
export function DeadlineList({ bids, now = new Date() }: DeadlineListProps) {
  const { overdue, upcoming } = partitionDeadlines(bids, now)
  const rows = [
    ...overdue.map((row) => ({ row, overdue: true })),
    ...upcoming.map((row) => ({ row, overdue: false })),
  ]
  const visible = rows.slice(0, ROW_CAP)
  const hidden = rows.length - visible.length

  return (
    <section data-testid="deadline-list">
      <SectionHeader
        title="Closing soon"
        aside={
          <span className="text-meta">
            {overdue.length > 0 ? `Overdue and next ${DEADLINE_WINDOW_DAYS} days` : `next ${DEADLINE_WINDOW_DAYS} days`}
          </span>
        }
      />
      <BoxedList>
        {rows.length === 0 ? (
          <BoxedListEmpty>No deadlines in the next {DEADLINE_WINDOW_DAYS} days.</BoxedListEmpty>
        ) : (
          <>
            {visible.map(({ row, overdue: isOverdue }) => (
              <DeadlineRowLink key={row.bid.id} row={row} overdue={isOverdue} />
            ))}
            {hidden > 0 && (
              <Link
                to="/app/bids"
                data-testid="deadline-more"
                className={cn("flex items-center gap-0.5 text-[13px] text-[hsl(var(--link))]", BOXED_ROW_CLASS)}
              >
                {hidden} more
                <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </Link>
            )}
          </>
        )}
      </BoxedList>
    </section>
  )
}
